import type { EventEnvelope, EventType } from '@shiocast/contracts';
import { getAccessToken, getCurrentStation } from './client';

const PATH = '/api/v2/realtime';

/** Longest gap between reconnection attempts. Beyond this, waiting longer helps nobody. */
const MAX_BACKOFF_MS = 30_000;

export interface RealtimeHandlers {
  /**
   * An event the panel should react to arrived. Called with the event type so a caller
   * can decide what to re-read.
   */
  onEvent: (type: EventType, envelope: EventEnvelope) => void;
  /**
   * The socket opened. Always take a fresh snapshot here: events that happened while
   * the connection was down were not queued for us, so the only way back to the truth
   * is to ask.
   */
  onOpen?: () => void;
  /** The socket closed and a reconnection is scheduled. */
  onClose?: () => void;
}

/**
 * The panel's live channel.
 *
 * The panel used to poll every ten seconds, which meant a track change could sit unseen
 * for ten seconds on a page whose entire job is saying what is on air, and meant the API
 * answered the same question over and over for every open tab. The gateway has been
 * broadcasting these events all along; nothing was listening.
 *
 * Two deliberate choices:
 *
 * An event is a signal to re-read, not the new state. Applying payloads directly would
 * make the panel's state a second implementation of what the API already computes, free
 * to drift from it; re-reading keeps one source of truth and still turns a ten-second
 * wait into about a hundred milliseconds.
 *
 * Polling does not go away, it becomes the fallback. A socket that cannot connect — a
 * proxy that will not upgrade, a captive network — must degrade to a panel that is
 * slower, never a panel that is wrong, so the timer keeps running whenever the socket is
 * not established.
 */
export class RealtimeChannel {
  private socket: WebSocket | null = null;
  private backoff = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private wanted = false;
  private open = false;

  constructor(private readonly handlers: RealtimeHandlers) {}

  /** Whether events are currently arriving. False means the caller should be polling. */
  get connected(): boolean {
    return this.open;
  }

  start(): void {
    this.wanted = true;
    this.connect();
  }

  stop(): void {
    this.wanted = false;
    clearTimeout(this.timer);
    this.backoff = 0;
    this.open = false;
    // Close deliberately, so the server drops the subscriber rather than waiting for a
    // heartbeat to notice. 1000 is a normal closure; it is not retried.
    this.socket?.close(1000, 'panel closed the channel');
    this.socket = null;
  }

  private url(): string | null {
    const token = getAccessToken();
    if (!token) return null;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = new URL(`${protocol}//${window.location.host}${PATH}`);
    url.searchParams.set('access_token', token);
    const station = getCurrentStation();
    if (station) url.searchParams.set('station', station);
    return url.toString();
  }

  private connect(): void {
    if (!this.wanted) return;
    const url = this.url();
    // No token yet means the panel is still signing in; the next start() will have one.
    if (!url) return;

    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.open = true;
      this.backoff = 0;
      this.handlers.onOpen?.();
    });

    socket.addEventListener('message', (message) => {
      try {
        const envelope = JSON.parse(String(message.data)) as EventEnvelope;
        if (envelope && typeof envelope.type === 'string') {
          this.handlers.onEvent(envelope.type, envelope);
        }
      } catch {
        // A frame we cannot read is the server's problem, not a reason to drop a
        // working connection; the next one is very likely fine.
      }
    });

    socket.addEventListener('close', () => {
      this.open = false;
      this.socket = null;
      this.handlers.onClose?.();
      this.scheduleReconnect();
    });

    // 'error' is always followed by 'close', which is where reconnection is handled.
    socket.addEventListener('error', () => {});
  }

  private scheduleReconnect(): void {
    if (!this.wanted) return;
    this.backoff = Math.min(this.backoff ? this.backoff * 2 : 1000, MAX_BACKOFF_MS);
    // Jitter, so a control plane coming back does not get every open panel at once.
    const wait = this.backoff + Math.floor(Math.random() * 500);
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.connect(), wait);
  }
}

/** Event types that change what the on-air page shows. */
export const ON_AIR_EVENTS: ReadonlySet<string> = new Set<EventType>([
  'station.now_playing.changed',
  'playout.track.started',
  'playout.track.ended',
  'playout.track.skipped',
  'playout.metadata.changed',
  'playout.queue.changed',
  'playout.fallback.activated',
  'live.connected',
  'live.disconnected',
]);

/** Event types that change the health grid. */
export const HEALTH_EVENTS: ReadonlySet<string> = new Set<EventType>([
  'runtime.started',
  'runtime.stopped',
  'runtime.output.connected',
  'runtime.output.disconnected',
  'broadcast.warning',
]);
